import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Sidebar } from "@/components/layout/Sidebar";
import { Navbar } from "@/components/layout/Navbar";
import { MobileBottomNav } from "@/components/layout/MobileBottomNav";
import { Breadcrumb, type Crumb } from "@/components/layout/Breadcrumb";
import { useAppStore } from "@/store/appStore";
import { cn } from "@/utils/format";
import { useMediaQuery } from "@/hooks/useMockQuery";

const pageMeta: Record<string, { title: string; crumbs: Crumb[] }> = {
  "/": {
    title: "CEO Dashboard",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "CEO Dashboard" },
    ],
  },
  "/system-settings": {
    title: "System Settings",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "System Settings", to: "/system-settings" },
    ],
  },
  "/system-settings/notifications": {
    title: "Notifications",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "System Settings", to: "/system-settings" },
      { label: "Notifications" },
    ],
  },
  "/system-settings/email": {
    title: "Email & SMTP",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "System Settings", to: "/system-settings" },
      { label: "Email & SMTP" },
    ],
  },
  "/system-settings/security": {
    title: "Security",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "System Settings", to: "/system-settings" },
      { label: "Security" },
    ],
  },
  "/system-settings/storage": {
    title: "Storage",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "System Settings", to: "/system-settings" },
      { label: "Storage" },
    ],
  },
  "/system-settings/backup": {
    title: "Backup & Recovery",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "System Settings", to: "/system-settings" },
      { label: "Backup & Recovery" },
    ],
  },
  "/system-settings/system": {
    title: "System Information",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "System Settings", to: "/system-settings" },
      { label: "System Information" },
    ],
  },
  "/profile": {
    title: "My Profile",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "My Profile" },
    ],
  },
  "/user-settings": {
    title: "User Settings",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "User Settings" },
    ],
  },
  "/appearance": {
    title: "Appearance",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Appearance" },
    ],
  },
  "/notification-preferences": {
    title: "Notification Preferences",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Notification Preferences" },
    ],
  },
  "/help": {
    title: "Help & Support",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Help & Support" },
    ],
  },
  "/business-development": {
    title: "What should I do next?",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Business Development" },
    ],
  },
  "/business-development/inquiries": {
    title: "Inquiries",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Business Development", to: "/business-development" },
      { label: "Inquiries" },
    ],
  },
  "/business-development/applications": {
    title: "Applications",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Business Development", to: "/business-development" },
      { label: "Applications" },
    ],
  },
  "/business-development/announcements": {
    title: "Announcements",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Business Development", to: "/business-development" },
      { label: "Announcements" },
    ],
  },
  "/business-development/announcements/submissions": {
    title: "Announcement Submissions",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Business Development", to: "/business-development" },
      { label: "Announcements", to: "/business-development/announcements" },
      { label: "Submissions" },
    ],
  },
  "/business-development/announcements/new": {
    title: "New Announcement",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Business Development", to: "/business-development" },
      { label: "Announcements", to: "/business-development/announcements" },
      { label: "New" },
    ],
  },
  "/business-development/crm": {
    title: "CRM",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Business Development", to: "/business-development" },
      { label: "CRM" },
    ],
  },
  "/sales-engine": {
    title: "Sales Engine",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Sales Engine" },
    ],
  },
  "/sales-engine/campaigns": {
    title: "Sales Engine > Campaigns",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Sales Engine", to: "/sales-engine" },
      { label: "Campaigns" },
    ],
  },
  "/sales-engine/templates": {
    title: "Sales Engine > Templates",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Sales Engine", to: "/sales-engine" },
      { label: "Templates" },
    ],
  },
  "/sales-engine/audience": {
    title: "Sales Engine > Audience",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Sales Engine", to: "/sales-engine" },
      { label: "Audience" },
    ],
  },
  "/sales-engine/successful-leads": {
    title: "Sales Engine > Successful Leads",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Sales Engine", to: "/sales-engine" },
      { label: "Successful Leads" },
    ],
  },
  "/sales-engine/dead-leads": {
    title: "Sales Engine > Dead Leads",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Sales Engine", to: "/sales-engine" },
      { label: "Dead Leads" },
    ],
  },
  "/sales-engine/analytics": {
    title: "Sales Engine > Analytics",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Sales Engine", to: "/sales-engine" },
      { label: "Analytics" },
    ],
  },
  "/meetings": {
    title: "Meetings",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Meetings" },
    ],
  },
  "/meetings/calendar": {
    title: "Meetings > Calendar",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Meetings", to: "/meetings" },
      { label: "Calendar" },
    ],
  },
  "/meetings/upcoming": {
    title: "Meetings > Upcoming",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Meetings", to: "/meetings" },
      { label: "Upcoming Meetings" },
    ],
  },
  "/meetings/internal": {
    title: "Meetings > Internal",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Meetings", to: "/meetings" },
      { label: "Internal Meetings" },
    ],
  },
  "/meetings/clients": {
    title: "Meetings > Client Meetings",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Meetings", to: "/meetings" },
      { label: "Client Meetings" },
    ],
  },
  "/meetings/sales": {
    title: "Meetings > Sales",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Meetings", to: "/meetings" },
      { label: "Sales Meetings" },
    ],
  },
  "/meetings/projects": {
    title: "Meetings > Projects",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Meetings", to: "/meetings" },
      { label: "Project Meetings" },
    ],
  },
  "/meetings/interviews": {
    title: "Meetings > Interviews",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Meetings", to: "/meetings" },
      { label: "Interviews" },
    ],
  },
  "/meetings/notes": {
    title: "Meetings > Notes",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Meetings", to: "/meetings" },
      { label: "Notes" },
    ],
  },
  "/meetings/recordings": {
    title: "Meetings > Recordings",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Meetings", to: "/meetings" },
      { label: "Recordings" },
    ],
  },
  "/meetings/follow-ups": {
    title: "Meetings > Follow-ups",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Meetings", to: "/meetings" },
      { label: "Follow-ups" },
    ],
  },
  "/meetings/action-items": {
    title: "Meetings > Action Items",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Meetings", to: "/meetings" },
      { label: "Action Items" },
    ],
  },
  "/meetings/templates": {
    title: "Meetings > Templates",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Meetings", to: "/meetings" },
      { label: "Templates" },
    ],
  },
  "/meetings/analytics": {
    title: "Meetings > Analytics",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Meetings", to: "/meetings" },
      { label: "Analytics" },
    ],
  },
  "/business-development/tasks": {
    title: "Tasks",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Business Development", to: "/business-development" },
      { label: "Tasks" },
    ],
  },
  "/business-development/documents": {
    title: "Sales Documents",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Business Development", to: "/business-development" },
      { label: "Sales Documents" },
    ],
  },
  "/business-development/documents/calculator": {
    title: "Cost Calculator",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Business Development", to: "/business-development" },
      { label: "Sales Documents", to: "/business-development/documents" },
      { label: "Cost Calculator" },
    ],
  },
  "/business-development/documents/quotations": {
    title: "Quotations",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Business Development", to: "/business-development" },
      { label: "Sales Documents", to: "/business-development/documents" },
      { label: "Quotations" },
    ],
  },
  "/business-development/documents/quotations/new": {
    title: "Create Quotation",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Business Development", to: "/business-development" },
      { label: "Quotations", to: "/business-development/documents/quotations" },
      { label: "Create" },
    ],
  },
  "/business-development/documents/proposals": {
    title: "Proposals",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Business Development", to: "/business-development" },
      { label: "Sales Documents", to: "/business-development/documents" },
      { label: "Proposals" },
    ],
  },
  "/business-development/documents/proposals/new": {
    title: "Proposal Builder",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Business Development", to: "/business-development" },
      { label: "Proposals", to: "/business-development/documents/proposals" },
      { label: "Builder" },
    ],
  },
  "/business-development/documents/contracts": {
    title: "Contracts",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Business Development", to: "/business-development" },
      { label: "Sales Documents", to: "/business-development/documents" },
      { label: "Contracts" },
    ],
  },
  "/business-development/documents/templates": {
    title: "Document Templates",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Business Development", to: "/business-development" },
      { label: "Sales Documents", to: "/business-development/documents" },
      { label: "Templates" },
    ],
  },
  "/business-development/reports": {
    title: "BD Reports",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Business Development", to: "/business-development" },
      { label: "Reports" },
    ],
  },
  "/finance": {
    title: "Finance Dashboard",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Finance" },
    ],
  },
  "/finance/income": {
    title: "Income",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Finance", to: "/finance" },
      { label: "Income" },
    ],
  },
  "/finance/expenses": {
    title: "Expenses",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Finance", to: "/finance" },
      { label: "Expenses" },
    ],
  },
  "/finance/invoices": {
    title: "Invoices",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Finance", to: "/finance" },
      { label: "Invoices" },
    ],
  },
  "/finance/transactions": {
    title: "Transactions",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Finance", to: "/finance" },
      { label: "Transactions" },
    ],
  },
  "/finance/cash-flow": {
    title: "Cash Flow",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Finance", to: "/finance" },
      { label: "Cash Flow" },
    ],
  },
  "/finance/reports": {
    title: "Finance Reports",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Finance", to: "/finance" },
      { label: "Reports" },
    ],
  },
  "/finance/settings": {
    title: "Finance Settings",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Finance", to: "/finance" },
      { label: "Settings" },
    ],
  },
  "/cms": {
    title: "Website CMS",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Website CMS" },
    ],
  },
  "/cms/homepage": {
    title: "Website CMS > Homepage",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Website CMS", to: "/cms" },
      { label: "Homepage" },
    ],
  },
  "/cms/portfolio": {
    title: "Website CMS > Portfolio",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Website CMS", to: "/cms" },
      { label: "Portfolio" },
    ],
  },
  "/cms/founder": {
    title: "Website CMS > Founder",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Website CMS", to: "/cms" },
      { label: "Founder" },
    ],
  },
  "/cms/company": {
    title: "Website CMS > Company Information",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Website CMS", to: "/cms" },
      { label: "Company Information" },
    ],
  },
  "/cms/social": {
    title: "Website CMS > Social Media",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Website CMS", to: "/cms" },
      { label: "Social Media" },
    ],
  },
  "/cms/analytics": {
    title: "Website CMS > Visitor Analytics",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Website CMS", to: "/cms" },
      { label: "Visitor Analytics" },
    ],
  },
  "/growth-strategy": {
    title: "Growth Strategy Center",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Growth Strategy Center" },
    ],
  },
  "/integrations": {
    title: "Integration Hub",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Integration Hub" },
    ],
  },
};

function resolvePageMeta(pathname: string): { title: string; crumbs: Crumb[] } {
  if (pageMeta[pathname]) return pageMeta[pathname];

  if (pathname.match(/^\/finance\/invoices\/[^/]+$/)) {
    return {
      title: "Invoice Details",
      crumbs: [
        { label: "Workspace", to: "/" },
        { label: "Finance", to: "/finance" },
        { label: "Invoices", to: "/finance/invoices" },
        { label: "Details" },
      ],
    };
  }

  if (pathname === "/cms/portfolio/new") {
    return {
      title: "Website CMS > Portfolio > Create Case Study",
      crumbs: [
        { label: "Workspace", to: "/" },
        { label: "Website CMS", to: "/cms" },
        { label: "Portfolio", to: "/cms/portfolio" },
        { label: "Create" },
      ],
    };
  }
  if (pathname.match(/^\/cms\/portfolio\/[^/]+$/)) {
    return {
      title: "Website CMS > Portfolio > Edit Case Study",
      crumbs: [
        { label: "Workspace", to: "/" },
        { label: "Website CMS", to: "/cms" },
        { label: "Portfolio", to: "/cms/portfolio" },
        { label: "Edit" },
      ],
    };
  }

  const se = "/sales-engine";
  if (pathname === `${se}/campaigns/new`) {
    return {
      title: "Sales Engine > Campaigns > Create Campaign",
      crumbs: [
        { label: "Workspace", to: "/" },
        { label: "Sales Engine", to: se },
        { label: "Campaigns", to: `${se}/campaigns` },
        { label: "Create Campaign" },
      ],
    };
  }
  if (pathname.startsWith(`${se}/campaigns/`)) {
    return {
      title: "Sales Engine > Campaigns > Details",
      crumbs: [
        { label: "Workspace", to: "/" },
        { label: "Sales Engine", to: se },
        { label: "Campaigns", to: `${se}/campaigns` },
        { label: "Details" },
      ],
    };
  }
  if (pathname.match(new RegExp(`^${se.replace(/\//g, "\\/")}/successful-leads/[^/]+$`))) {
    return {
      title: "Sales Engine > Successful Leads > Campaign",
      crumbs: [
        { label: "Workspace", to: "/" },
        { label: "Sales Engine", to: se },
        { label: "Successful Leads", to: `${se}/successful-leads` },
        { label: "Campaign" },
      ],
    };
  }
  if (pathname.match(new RegExp(`^${se.replace(/\//g, "\\/")}/dead-leads/[^/]+$`))) {
    return {
      title: "Sales Engine > Dead Leads > Campaign",
      crumbs: [
        { label: "Workspace", to: "/" },
        { label: "Sales Engine", to: se },
        { label: "Dead Leads", to: `${se}/dead-leads` },
        { label: "Campaign" },
      ],
    };
  }
  if (pathname === `${se}/audience/import`) {
    return {
      title: "Sales Engine > Audience > Import",
      crumbs: [
        { label: "Workspace", to: "/" },
        { label: "Sales Engine", to: se },
        { label: "Audience", to: `${se}/audience` },
        { label: "Import" },
      ],
    };
  }
  if (pathname.match(new RegExp(`^${se.replace(/\//g, "\\/")}/audience/[^/]+$`))) {
    return {
      title: "Sales Engine > Audience > Detail",
      crumbs: [
        { label: "Workspace", to: "/" },
        { label: "Sales Engine", to: se },
        { label: "Audience", to: `${se}/audience` },
        { label: "Detail" },
      ],
    };
  }
  if (pathname.startsWith(`${se}/templates/`)) {
    return {
      title: "Sales Engine > Templates > Builder",
      crumbs: [
        { label: "Workspace", to: "/" },
        { label: "Sales Engine", to: se },
        { label: "Templates", to: `${se}/templates` },
        { label: "Builder" },
      ],
    };
  }
  if (pathname.startsWith(se)) {
    return {
      title: "Sales Engine",
      crumbs: [
        { label: "Workspace", to: "/" },
        { label: "Sales Engine", to: se },
      ],
    };
  }

  const bd = "/business-development";
  const inquiryMatch = pathname.match(/^\/business-development\/inquiries\/([^/]+)$/);
  if (inquiryMatch) {
    return {
      title: "Inquiry Details",
      crumbs: [
        { label: "Workspace", to: "/" },
        { label: "Business Development", to: bd },
        { label: "Inquiries", to: `${bd}/inquiries` },
        { label: inquiryMatch[1] },
      ],
    };
  }
  const applicationMatch = pathname.match(/^\/business-development\/applications\/([^/]+)$/);
  if (applicationMatch) {
    return {
      title: "Application Review",
      crumbs: [
        { label: "Workspace", to: "/" },
        { label: "Business Development", to: bd },
        { label: "Applications", to: `${bd}/applications` },
        { label: applicationMatch[1] },
      ],
    };
  }
  const crmWorkspaceTitles: Record<string, string> = {
    [`${bd}/crm/leads`]: "Leads",
    [`${bd}/crm/clients`]: "Clients",
    [`${bd}/crm/projects`]: "Projects",
  };
  if (crmWorkspaceTitles[pathname]) {
    return {
      title: crmWorkspaceTitles[pathname],
      crumbs: [
        { label: "Workspace", to: "/" },
        { label: "Business Development", to: bd },
        { label: "CRM", to: `${bd}/crm` },
        { label: crmWorkspaceTitles[pathname] },
      ],
    };
  }
  const clientMatch = pathname.match(/^\/business-development\/crm\/clients\/([^/]+)$/);
  if (clientMatch) {
    return {
      title: "Client Profile",
      crumbs: [
        { label: "Workspace", to: "/" },
        { label: "Business Development", to: bd },
        { label: "CRM", to: `${bd}/crm` },
        { label: "Clients", to: `${bd}/crm/clients` },
        { label: clientMatch[1] },
      ],
    };
  }
  if (pathname.startsWith(`${bd}/crm/leads/`)) {
    const leadMatch = pathname.match(/^\/business-development\/crm\/leads\/([^/]+)$/);
    return {
      title: "Lead Details",
      crumbs: [
        { label: "Workspace", to: "/" },
        { label: "Business Development", to: bd },
        { label: "CRM", to: `${bd}/crm` },
        { label: "Leads", to: `${bd}/crm/leads` },
        { label: leadMatch?.[1] ?? "Details" },
      ],
    };
  }
  if (pathname.startsWith(`${bd}/crm/companies/`)) {
    return {
      title: "Company Profile",
      crumbs: [
        { label: "Workspace", to: "/" },
        { label: "CRM", to: `${bd}/crm` },
        { label: "Company" },
      ],
    };
  }
  if (pathname.startsWith(`${bd}/crm/contacts/`)) {
    return {
      title: "Contact Profile",
      crumbs: [
        { label: "Workspace", to: "/" },
        { label: "CRM", to: `${bd}/crm` },
        { label: "Contact" },
      ],
    };
  }
  if (pathname.match(/^\/meetings\/[^/]+$/) && !pageMeta[pathname]) {
    return {
      title: "Meeting Details",
      crumbs: [
        { label: "Workspace", to: "/" },
        { label: "Meetings", to: "/meetings" },
        { label: "Details" },
      ],
    };
  }
  if (pathname.startsWith(`${bd}/announcements/`)) {
    return {
      title: "Edit Announcement",
      crumbs: [
        { label: "Workspace", to: "/" },
        { label: "Business Development", to: "/business-development" },
        { label: "Announcements", to: "/business-development/announcements" },
        { label: "Edit" },
      ],
    };
  }
  if (pathname.startsWith(`${bd}/meetings/`)) {
    return {
      title: "Meeting Details",
      crumbs: [
        { label: "Workspace", to: "/" },
        { label: "Meetings", to: "/meetings" },
        { label: "Details" },
      ],
    };
  }
  if (pathname.startsWith(`${bd}/tasks/`)) {
    return {
      title: "Task Details",
      crumbs: [
        { label: "Workspace", to: "/" },
        { label: "Tasks", to: `${bd}/tasks` },
        { label: "Details" },
      ],
    };
  }
  if (pathname.match(new RegExp(`^${bd}/documents/quotations/[^/]+$`))) {
    return {
      title: "Quotation Details",
      crumbs: [
        { label: "Workspace", to: "/" },
        { label: "Quotations", to: `${bd}/documents/quotations` },
        { label: "Details" },
      ],
    };
  }
  if (pathname.match(new RegExp(`^${bd}/documents/proposals/[^/]+/edit$`))) {
    return {
      title: "Proposal Builder",
      crumbs: [
        { label: "Workspace", to: "/" },
        { label: "Proposals", to: `${bd}/documents/proposals` },
        { label: "Builder" },
      ],
    };
  }
  if (pathname.match(new RegExp(`^${bd}/documents/proposals/[^/]+$`))) {
    return {
      title: "Proposal Details",
      crumbs: [
        { label: "Workspace", to: "/" },
        { label: "Proposals", to: `${bd}/documents/proposals` },
        { label: "Details" },
      ],
    };
  }
  if (pathname.match(new RegExp(`^${bd}/documents/contracts/[^/]+$`))) {
    return {
      title: "Contract Details",
      crumbs: [
        { label: "Workspace", to: "/" },
        { label: "Contracts", to: `${bd}/documents/contracts` },
        { label: "Details" },
      ],
    };
  }
  if (pathname.startsWith(bd)) {
    return {
      title: "Business Development",
      crumbs: [
        { label: "Workspace", to: "/" },
        { label: "Business Development", to: bd },
      ],
    };
  }

  return {
    title: "OPERA OS",
    crumbs: [
      { label: "Workspace", to: "/" },
      { label: "Module" },
    ],
  };
}

export function AppShell() {
  const theme = useAppStore((s) => s.theme);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width: 1024px)");
  const location = useLocation();

  useEffect(() => {
    const resolved =
      theme === "system"
        ? window.matchMedia("(prefers-color-scheme: dark)").matches
          ? "dark"
          : "light"
        : theme;
    document.documentElement.setAttribute("data-theme", resolved);

    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      document.documentElement.setAttribute(
        "data-theme",
        mq.matches ? "dark" : "light"
      );
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [theme]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const meta = resolvePageMeta(location.pathname);

  return (
    <div
      className={cn(
        "app-shell",
        collapsed && !isMobile && "app-shell--collapsed",
        mobileOpen && "app-shell--mobile-open"
      )}
    >
      <div className="app-shell__sidebar">
        <Sidebar
          collapsed={!isMobile && collapsed}
          onToggleCollapse={() => {
            if (isMobile) setMobileOpen(false);
            else setCollapsed((v) => !v);
          }}
          onNavigate={() => setMobileOpen(false)}
        />
      </div>
      {isMobile && mobileOpen ? (
        <button
          type="button"
          className="app-shell__overlay"
          aria-label="Close navigation"
          onClick={() => setMobileOpen(false)}
        />
      ) : null}
      <Navbar title={meta.title} onMenuClick={() => setMobileOpen(true)} />
      <main className="app-shell__main">
        <div className="page-enter">
          <Breadcrumb items={meta.crumbs} />
          <Outlet />
        </div>
      </main>
      <MobileBottomNav />
    </div>
  );
}
